<script setup lang="ts">
import SvgButton from "~/components/Common/Buttons/SvgButton.vue";
import { Editor } from "@tiptap/vue-3";
import type { EditorVariable } from "~/utils/options/editor.options";

const props = defineProps({
  editor: {
    type: Object as PropType<Editor>,
  },
  variables: {
    type: Array as PropType<EditorVariable[]>,
    default: () => [],
  },
});

const isOpen = ref(false);
const buttonRef = ref();
const popoverRef = ref();

function toggleDropdown() {
  isOpen.value = !isOpen.value;
}

function closeDropdown(event: Event) {
  const buttonElement = buttonRef.value?.$el || buttonRef.value;
  const popoverElement = popoverRef.value;
  if (
    isOpen.value &&
    event.target &&
    buttonElement &&
    !buttonElement.contains(event.target as Node) &&
    (!popoverElement || !popoverElement.contains(event.target as Node))
  ) {
    isOpen.value = false;
  }
}

function extractKey(name: string): string {
  return name.replace(/^\{\{|\}\}$/g, "");
}

function insertVariable(variable: EditorVariable) {
  const key = extractKey(variable.name);
  props.editor
    ?.chain()
    .focus()
    .insertVariableBlock({
      variable: key,
      label: variable.signification,
    })
    .run();
  isOpen.value = false;
}

onMounted(() => document.addEventListener("click", closeDropdown));
onUnmounted(() => document.removeEventListener("click", closeDropdown));
</script>

<template>
  <div class="variable-dropdown-container">
    <SvgButton
      ref="buttonRef"
      class="button-container"
      name="variable-image"
      :width="18"
      :height="18"
      @click.stop.prevent="toggleDropdown"
    />
    <div v-if="isOpen" ref="popoverRef" class="variable-popover" @click.stop>
      <div class="variable-popover-header">
        <span class="variable-popover-title">Insérer une variable image</span>
      </div>
      <ul class="variable-list">
        <li v-for="variable in variables" :key="variable.name" class="variable-item" @click="insertVariable(variable)">
          <div class="variable-item-row">
            <span class="variable-badge">{{ variable.name }}</span>
            <span class="variable-desc">{{ variable.signification }}</span>
          </div>
        </li>
        <li v-if="variables.length === 0" class="variable-empty">Aucune variable disponible</li>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
.variable-dropdown-container {
  @apply relative;
}

.button-container {
  @apply flex-row justify-center items-center rounded-[12px] w-fit h-9 min-w-9 cursor-pointer border border-stroke-color text-secondary-color transition-colors duration-300;

  &:hover,
  &:hover * {
    @apply bg-lightest-grey-color border-stroke-grey-color;
  }

  &:active,
  &:active * {
    @apply border-[var(--blue-deep-dark)] text-[var(--blue-deep-dark)];
  }
}

.variable-popover {
  @apply absolute top-[45px] left-0 w-[300px] max-h-[260px] z-[501] overflow-hidden flex flex-col;
  background: var(--white-main);
  box-shadow: var(--shadow-medium);
  border-radius: 8px;
}

.variable-popover-header {
  @apply py-[10px] px-3 border-b border-stroke-color;
}

.variable-popover-title {
  @apply text-[13px] font-semibold text-primary-color;
}

.variable-list {
  @apply list-none m-0 p-1 overflow-y-auto flex-1;
}

.variable-item {
  @apply p-2 rounded-md cursor-pointer;

  &:hover {
    @apply bg-light-grey-color;
  }
}

.variable-item-row {
  @apply flex flex-col gap-[2px];
}

.variable-badge {
  @apply text-[11px] font-semibold text-secondary-color bg-stroke-color py-[2px] px-[6px] rounded w-fit;
}

.variable-desc {
  @apply text-xs text-secondary-color;
}

.variable-empty {
  @apply py-4 px-2 text-center text-xs text-tertiary-color;
}
</style>
